/**
 * VoiceToTextSDK 核心入口
 *
 * 对外只暴露:
 *
 * appendText()
 * finish()
 * play()
 * stop()
 * reset()
 * close()
 * onPlaybackComplete()
 *
 * 外部不关心:
 * TTS厂商
 * WebSocket
 * PCM播放
 */

import TextManager from "./TextManager";
import SentenceQueue from "./SentenceQueue";
import PlaybackManager from "./PlaybackManager";
import TaskManager from "./TaskManager";

import AudioPlayer from "../player/AudioPlayer";

import ProviderFactory from "../providers/ProviderFactory";
import {normalizeTTSMarkdown} from "./TTSMarkdownNormalizer";

export default class VoiceToTextSDK {
    constructor(config = {}) {
        this.config = config;
        // 文本管理
        this.textManager = new TextManager();
        // 等待播放句子
        this.sentenceQueue = new SentenceQueue();
        // 播放状态
        this.playback = new PlaybackManager();
        // 任务管理
        this.taskManager = new TaskManager();
        // 音频播放器
        this.player = new AudioPlayer();
        // 创建TTS服务
        this.provider = ProviderFactory.create(config.provider, config);
        this.currentSpeaking = false;
        this.onPlaybackCompleteCallback = null;
        // 播放完成标志：需要 provider 已结束且 player 已播放完所有缓冲音频
        this._providerFinished = false;
        this._playbackDrained = false;
        this.bind();
    }

    /**
     * 绑定内部事件
     */
    bind() {
        // 文本切句后进入队列
        this.textManager.onSentence =
            sentence => {
                console.log("[SDK] sentence:", sentence);
                this.sentenceQueue.push(sentence);

                // 如果正在播放
                // 新句子自动继续
                if (this.playback.isPlaying()) {
                    this.consume();
                }
            };

        // TTS返回音频
        this.provider.setAudioCallback(
            buffer => {
                // 收到新音频表示播放尚未真正结束，重置 drained 标志
                this._playbackDrained = false;
                this.player.push(buffer);
            }
        );

        // 客户端音频缓冲播放完毕
        this.player.setOnDrainedCallback(
            () => {
                console.log("[SDK] playback drained");
                this._playbackDrained = true;
                this._checkPlaybackComplete();
            }
        );

        // 当前一句完成
        this.provider.setCompleteCallback(
            () => {
                this.currentSpeaking = false;
                console.log("[SDK] sentence complete");
                this.consume();
                // 所有句子消费完毕，标记为空闲状态
                if (this.sentenceQueue.isEmpty()) {
                    this.playback.idle();
                }
                this._providerFinished = true;
                this._checkPlaybackComplete();
            }
        );

        this.provider.setErrorCallback(
            err => {
                console.error("[SDK] tts error", err);
                this.currentSpeaking = false;
                if (this.playback.isPlaying()) {
                    this.consume();
                }
            }
        );
    }

    /**
     * 检查是否真正播放完成
     *
     * 需要同时满足：
     * 1. provider 已经发送完所有音频
     * 2. player 已经播放完所有缓冲音频
     *
     * 避免音频 chunk 之间的间隙导致 player 提前触发播放完成。
     */
    _checkPlaybackComplete() {
        if (this._providerFinished && this._playbackDrained) {
            console.log("[SDK] playback complete");
            this.onPlaybackCompleteCallback?.();
            this._providerFinished = false;
            this._playbackDrained = false;
        }
    }

    /**
     * 接收AI流文本
     *
     * 你的while循环调用这里
     */
    appendText(text) {
        this.textManager.append(text);
    }

    /**
     * AI生成结束
     */
    finish() {
        this.textManager.finish();
        this.provider.finish?.();
    }

    /**
     * 用户点击播放
     */
    play() {
        console.log("[SDK] play");

        // 重置播放完成标志
        this._providerFinished = false;
        this._playbackDrained = false;

        // 重置并从头开始播放
        this.sentenceQueue.clear();
        this.provider.stop();
        this.player.stop();

        if (this.textManager.getText()) {
            this.textManager.rebuild();
        }

        this.player.resume();
        this.playback.start();
        this.consume();
    }

    /**
     * 消费文本队列
     *
     * 一句一句交给TTS
     */
    consume(){
        if(!this.playback.isPlaying()){
            return;
        }

        if(this.currentSpeaking){
            return;
        }

        let text=this.sentenceQueue.shift();
        let cleanText="";

        while(text!==undefined){
            cleanText = normalizeTTSMarkdown(text);

            if(cleanText){
                break;
            }
            text=this.sentenceQueue.shift();
        }

        if(!cleanText){
            return;
        }
        this.currentSpeaking=true;
        console.log("[SDK] sentence:", cleanText);
        this.provider.speak(cleanText);

        if(this.provider.isStreaming?.()){
            this.currentSpeaking=false;
            this.consume();
        }
    }

    cleanText(text){
        return text
            .replace(/[#>*|_-]/g,"")
            .replace(/\n+/g,"")
            .trim();
    }

    /**
     * 停止播放
     *
     * 不保存位置
     */
    stop() {
        console.log("[SDK] stop");
        this.playback.stop();
        // 当前句结束标记
        this.currentSpeaking = false;
        // 清等待播放队列
        this.sentenceQueue.clear();
        // 关闭TTS连接
        this.provider.stop();
        // 停止音频
        this.player.stop();
        // 重置播放完成标志，避免旧任务回调影响新任务
        this._providerFinished = false;
        this._playbackDrained = false;
    }

    /**
     * 重新播放
     *
     * 从全文开始
     */
    replay() {
        this.stop();
        this.sentenceQueue.clear();
        this.textManager.rebuild();
        this.play();
    }

    /**
     * 从头朗读指定文本（流式中重新打开朗读开关时使用）
     *
     * 与 play()/replay() 的区别：
     * play()/replay() 基于 textManager 已有的全文重建；
     * restart() 会用指定文本替换 textManager 内容后，再从头播放。
     */
    restart(text) {
        console.log("[SDK] restart");
        this.taskManager.reset();
        this.stop();
        this.textManager.clear();
        if (text) {
            this.textManager.append(text);
        }
        this.play();
    }

    /**
     * 新任务
     *
     * 用户重新提问
     */
    reset() {
        console.log("[SDK] reset");
        this.taskManager.reset();
        this.stop();
        this.textManager.clear();
        this._providerFinished = false;
        this._playbackDrained = false;
    }

    /**
     * 页面销毁
     */
    close() {
        this.stop();
        this.provider.close();
        this.player.close();
    }

    /**
     * 是否正在播放
     */
    isPlaying() {
        return this.playback.isPlaying();
    }

    /**
     * 注册播放完毕回调
     *
     * 与 SynthesisCompleted 不同：
     * SynthesisCompleted 表示服务端合成完毕（音频可能还在缓冲播放）
     * 此回调表示客户端音频真正播放完成
     *
     * @param {Function} callback
     */
    onPlaybackComplete(callback) {
        this.onPlaybackCompleteCallback = callback;
    }
}
